import type { LucideIcon } from "lucide-react";

import React, { useState } from "react";

import {
  Button as BaseButton,
  buttonVariants,
  type ButtonSize,
  type ButtonVariant,
} from "./base/button";
import { Tooltip } from "./base/tooltip";
import { useFormContext } from "./form/form";

type Props = Omit<
  React.ComponentProps<typeof BaseButton>,
  | "variant"
  | "size"
  | "destructive"
  | "success"
  | "iconOnly"
  | "children"
  | "onClick"
> & {
  /**
   * The button's role, not its look: `primary` is the one action that
   * completes its area, `secondary` (the default) an ordinary action,
   * `tertiary` a table-row or inline action.
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** For actions that delete, remove, discard or revoke — at any level. */
  destructive?: boolean;
  /** Tertiary only: a confirming row or inline action — Approve, Restore. */
  success?: boolean;
  /** Leading icon; without children the button is icon-only. */
  icon?: LucideIcon;
  /**
   * Hover text. An icon-only button shows its `label` unless this overrides
   * it; a text button shows nothing unless given one. Use the override where
   * the name has to identify the row it acts on ("Remove Bisoprolol") but the
   * tooltip only needs the verb, and `null` to suppress it, e.g. when the
   * caller wraps the button in an explanatory tooltip of its own. Disabled
   * buttons keep theirs, so it can say why.
   */
  tooltip?: string | null;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => void | Promise<unknown>;
} & (
    | { children: React.ReactNode; label?: never }
    | {
        children?: never;
        icon: LucideIcon;
        /** Accessible name, and the default tooltip. */
        label: string;
      }
  );

export function Button({
  variant,
  size,
  destructive,
  success,
  icon: Icon,
  label,
  tooltip,
  pending: propsPending,
  onClick,
  children,
  asChild,
  type = "button",
  ...props
}: Props) {
  const formContext = useFormContext();
  const [localPending, setLocalPending] = useState(false);

  const pending =
    propsPending ||
    localPending ||
    (type === "submit" && !!formContext?.pending);

  const button = (
    <BaseButton
      aria-label={label}
      {...props}
      asChild={asChild}
      // A Slot child is usually a link, where `type` means something else.
      type={asChild ? undefined : type}
      variant={variant}
      size={size}
      destructive={destructive}
      success={success}
      iconOnly={label !== undefined}
      pending={pending}
      onClick={(event) => {
        const returnValue = onClick?.(event);

        if (returnValue instanceof Promise) {
          setLocalPending(true);
          void returnValue.finally(() => {
            setLocalPending(false);
          });
        }
      }}
    >
      {asChild ? (
        children
      ) : (
        <>
          {Icon && <Icon aria-hidden />}
          {children}
        </>
      )}
    </BaseButton>
  );

  // Only wrap in a tooltip when there's something to show; otherwise the button
  // renders bare (no Radix tooltip machinery, no provider dependency).
  const hover = tooltip === undefined ? label : tooltip;
  return hover ? <Tooltip content={hover}>{button}</Tooltip> : button;
}

export { buttonVariants };
